import { useState } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { Trash } from '@phosphor-icons/react/dist/ssr/Trash';
import ConnectionCard from './ConnectionCard';
import { COLORS } from '../../../styles/UISystem';
import { getConnectionDisplayName } from '../../../utils/connectionName';
import { BottomSheet } from '../../UISystem/Overlays/BottomSheet';
import { ButtonRounded } from '../../UISystem/Buttons/ButtonRounded';

/**
 * SwipeableConnectionCard Component
 * Swipe left to reveal delete button
 */
const DELETE_BUTTON_WIDTH = 80;
const SWIPE_THRESHOLD = 50;

const SwipeableConnectionCard = ({
  connection,
  onDelete,
  onProfileClick,
  onChatClick,
  onCardClick,
  getFormattedLocation,
  showMarginBottom,
  recentMessage,
  isMessageFromUser,
  isManualContact = false,
  hasUnreadMessage = false,
  displayTimestamp,
  liveNeighborhood,
  isRoyaleUser = false,
  variant = 'connection',
  // Connection variant props
  nickname = null,
  groups = [],
  locationDisplay = null,
  hometown = null,
  photoThumbUrl = null,
  // Rendered INSIDE the swiping layer at the card's right edge, so it
  // travels with the card on swipe instead of squatting over the revealed
  // delete button (TEXT camera overlay, device pass 1j)
  trailingOverlay = null,
}) => {
  const x = useMotionValue(0);
  const deleteOpacity = useTransform(x, [-DELETE_BUTTON_WIDTH, 0], [1, 0]);
  const [isOpen, setIsOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleDragEnd = (event, info) => {
    // If swiped past threshold, snap to show delete button
    if (info.offset.x < -SWIPE_THRESHOLD) {
      animate(x, -DELETE_BUTTON_WIDTH, { type: 'spring', stiffness: 500, damping: 30 });
      setIsOpen(true);
    } else {
      // Snap back to closed
      animate(x, 0, { type: 'spring', stiffness: 500, damping: 30 });
      setIsOpen(false);
    }
  };

  const snapClosed = () => {
    animate(x, 0, { type: 'spring', stiffness: 500, damping: 30 });
    setIsOpen(false);
  };

  // Trash disc tap → ask first (CD #15: deleting must be fully intentional; an
  // accidental swipe is undone with the easy Keep tap). No immediate delete.
  const handleDelete = () => {
    setShowConfirm(true);
  };

  // Confirmed: animate the card off screen, then delete.
  const handleConfirmDelete = () => {
    setShowConfirm(false);
    animate(x, -window.innerWidth, { duration: 0.2 }).then(() => {
      onDelete();
    });
  };

  // Keep / tap-outside: dismiss the sheet and snap the card closed.
  const handleKeep = () => {
    setShowConfirm(false);
    snapClosed();
  };

  const handleTap = () => {
    // Only reset if already open (don't interfere with drag)
    if (isOpen) {
      snapClosed();
    }
  };

  const isChat = variant === 'chat';
  const displayName = getConnectionDisplayName(connection);

  return (
    <div
      style={{
        position: 'relative',
        overflow: 'hidden',
        marginBottom: isChat && showMarginBottom ? '16px' : '0',
      }}
    >
      {/* Delete button (revealed on swipe) - red circle with trash icon */}
      <motion.div
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: DELETE_BUTTON_WIDTH,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: deleteOpacity,
        }}
      >
        <div
          onClick={handleDelete}
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: '#FF3B30',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <Trash size={24} color="white" weight="bold" />
        </div>
      </motion.div>

      {/* Swipeable card container */}
      <motion.div
        style={{
          x,
          backgroundColor: COLORS.background,
          position: 'relative',
          zIndex: 1,
        }}
        drag="x"
        dragConstraints={{ left: -DELETE_BUTTON_WIDTH, right: 0 }}
        // Left-only (CD #15): a little give when over-pulling left, none to the
        // right — the card no longer springs rightward past its home position.
        dragElastic={{ top: 0, bottom: 0, left: 0.1, right: 0 }}
        onDragEnd={handleDragEnd}
        onTap={handleTap}
      >
        <ConnectionCard
          onChatClick={onChatClick}
          name={displayName || 'Add name'}
          isPlaceholderName={!displayName}
          location={getFormattedLocation(connection.connectionMetadata)}
          photoUrl={connection.anonData?.photoUrl}
          timestamp={displayTimestamp}
          recentMessage={recentMessage}
          isMessageFromUser={isMessageFromUser}
          hasUnreadMessage={hasUnreadMessage}
          isManualContact={isManualContact}
          isRoyaleUser={isRoyaleUser}
          onProfileClick={onProfileClick}
          onCardClick={onCardClick}
          liveNeighborhood={liveNeighborhood}
          variant={variant}
          nickname={nickname}
          groups={groups}
          locationDisplay={locationDisplay}
          hometown={hometown}
          photoThumbUrl={photoThumbUrl}
        />
        {trailingOverlay && (
          <div
            style={{
              position: 'absolute',
              right: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            {trailingOverlay}
          </div>
        )}
      </motion.div>

      {/* Confirm sheet (CD #15 / CD Drop 8 Row D, FINALS 4.41-4.43): the big
          black button KEEPS them; Delete is the quiet one under it; tap-outside
          keeps. Built on the app BottomSheet + ButtonRounded (primary = black,
          blank = de-emphasized), matching the design's .sf-sheet treatment. */}
      {showConfirm && (
        <BottomSheet onClose={handleKeep} style={{ padding: '26px 20px 34px' }}>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '16px', textAlign: 'center' }}
          >
            <div style={{ fontSize: '20px', fontWeight: 600, color: COLORS.CHAT_TEXT_DARK }}>
              Delete {displayName || 'this person'}?
            </div>
            <div
              style={{
                margin: '-8px 0 2px',
                fontSize: '15px',
                fontWeight: 300,
                lineHeight: 1.45,
                color: COLORS.DARK_GRAY,
              }}
            >
              They&rsquo;ll be removed permanently.
            </div>
            <ButtonRounded text="Keep" size="full" variant="primary" onClick={handleKeep} />
            <ButtonRounded
              text="Delete"
              size="full"
              variant="blank"
              onClick={handleConfirmDelete}
              style={{ color: COLORS.CHAT_TEXT_DARK, fontSize: '17px', height: '44px' }}
            />
          </div>
        </BottomSheet>
      )}
    </div>
  );
};

export default SwipeableConnectionCard;
